import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CalendarCheck,
  Check,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { Button, Select, Switch } from '@/components/ui';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { calendarManager } from '../../../business/calendar/CalendarManager';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  calendarDisplayName,
  canAdvanceCalendarStep,
  defaultSelectedCalendarIds,
  resolveAccountEmail,
  resolveCalendarStage,
  sortCalendarsForPicker,
} from '../../../business/onboarding/googleCalendarSetup';
import type { Routemize_DTOs_CalendarIntegrationInfoDto } from '../../../api/models/Routemize_DTOs_CalendarIntegrationInfoDto';
import type { Routemize_DTOs_GoogleCalendarDto } from '../../../api/models/Routemize_DTOs_GoogleCalendarDto';
import { Routemize_Entities_Calendar_External_ExternalCalendarSyncDirection as SyncDirection } from '../../../api/models/Routemize_Entities_Calendar_External_ExternalCalendarSyncDirection';
import {
  CALENDAR_OAUTH_RESULT,
  CALENDAR_OAUTH_RETURN_PATH,
  prefersSameTabOAuth,
  type CalendarOAuthResult,
} from '../../../utils/calendarOAuth';
import { Callout, StepIntro } from '../shared/StepShell';

/** Outcome of the last attempt to read the account's calendar list. */
type PickerState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

const GoogleMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.06 12.25c0-.85-.08-1.67-.22-2.45H12v4.63h6.2a5.3 5.3 0 0 1-2.3 3.48v2.89h3.72c2.18-2 3.44-4.96 3.44-8.55Z"
    />
    <path
      fill="#34A853"
      d="M12 23.5c3.11 0 5.72-1.03 7.62-2.8l-3.72-2.88c-1.03.69-2.35 1.1-3.9 1.1-3 0-5.545-2.03-6.45-4.75H1.7v2.98A11.5 11.5 0 0 0 12 23.5Z"
    />
    <path
      fill="#FBBC05"
      d="M5.55 14.17a6.9 6.9 0 0 1 0-4.34V6.85H1.7a11.5 11.5 0 0 0 0 10.3l3.85-2.98Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.69 0 3.21.58 4.4 1.72l3.3-3.3C17.71 1.27 15.1.25 12 .25A11.5 11.5 0 0 0 1.7 6.85l3.85 2.98C6.455 7.11 9 4.75 12 4.75Z"
    />
  </svg>
);

export const CalendarStep: React.FC = () => {
  const { completeStep } = useOnboarding();

  const [info, setInfo] = useState<Routemize_DTOs_CalendarIntegrationInfoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isBinding, setIsBinding] = useState(false);
  const [hasSkipped, setHasSkipped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [available, setAvailable] = useState<Routemize_DTOs_GoogleCalendarDto[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickerState, setPickerState] = useState<PickerState>('idle');

  const syncedCalendars = info?.connectedCalendars ?? [];
  const stage = resolveCalendarStage({
    isLoading,
    isAuthorized: Boolean(info?.isConnectedToGoogle),
    syncedCalendarCount: syncedCalendars.length,
  });

  useStepValidity(
    canAdvanceCalendarStep({
      stage,
      syncedCalendarCount: syncedCalendars.length,
      hasSkipped,
    })
  );

  useStepSaver(
    ONBOARDING_STEP.CALENDAR,
    useCallback(async () => {
      completeStep(ONBOARDING_STEP.CALENDAR);
    }, [completeStep])
  );

  /** Read the account's calendar list so it can be shown inline. */
  const loadAvailableCalendars = useCallback(async () => {
    setPickerState('loading');
    try {
      const calendars = await calendarManager.getAvailableGoogleCalendars();
      const list = sortCalendarsForPicker(calendars ?? []);
      setAvailable(list);
      if (list.length === 0) {
        setPickerState('empty');
        return;
      }
      setSelectedIds(current => (current.length > 0 ? current : defaultSelectedCalendarIds(list)));
      setPickerState('ready');
    } catch (err) {
      console.error('Failed to load Google calendars:', err);
      setPickerState('error');
    }
  }, []);

  const refreshIntegration = useCallback(async () => {
    const next = await calendarManager.getCalendarIntegrationInfo(true);
    setInfo(next);
    return next;
  }, []);

  const completeAuthorization = useCallback(
    async (code: string, state: string) => {
      setIsAuthorizing(true);
      setError(null);
      try {
        const result = await calendarManager.completeAuthorization(code, state);
        if (!result.success) {
          setError(
            result.errorMessage ||
              "Google didn't complete the connection. Please try again and tick the calendar permission."
          );
          return;
        }

        calendarManager.invalidateIntegrationInfo();
        await refreshIntegration();

        const calendars = sortCalendarsForPicker(result.availableCalendars ?? []);
        if (calendars.length === 0) {
          // Authorization succeeded but no calendars came back, which almost
          // always means the calendar permission checkbox was left unticked.
          setPickerState('empty');
          return;
        }
        setAvailable(calendars);
        setSelectedIds(defaultSelectedCalendarIds(calendars));
        setPickerState('ready');
      } catch (err) {
        console.error('Failed to complete Google authorization:', err);
        setError('We could not finish connecting your Google account. Please try again.');
      } finally {
        setIsAuthorizing(false);
      }
    },
    [refreshIntegration]
  );

  // A same-tab redirect leaves the result in sessionStorage and lands back on
  // /onboarding, where the wizard restores this step from saved progress.
  const consumedOAuthRef = useRef(false);
  useEffect(() => {
    if (consumedOAuthRef.current) return;
    consumedOAuthRef.current = true;

    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(CALENDAR_OAUTH_RESULT);
      if (raw) sessionStorage.removeItem(CALENDAR_OAUTH_RESULT);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const result = JSON.parse(raw) as CalendarOAuthResult;
      if (result.type === 'OAUTH_SUCCESS') {
        completeAuthorization(result.code, result.state);
      } else {
        setError('Google sign-in was cancelled or failed. You can try again below.');
      }
    } catch {
      // Malformed payload — nothing useful to resume.
    }
  }, [completeAuthorization]);

  useEffect(() => {
    let cancelled = false;

    calendarManager
      .getCalendarIntegrationInfo(true)
      .then(next => {
        if (cancelled) return;
        setInfo(next);
        // Authorized with nothing bound is the state that used to dead-end.
        // Load the list straight away so the choice is on screen rather than
        // behind a link the user has to notice.
        if (next?.isConnectedToGoogle && (next.connectedCalendars?.length ?? 0) === 0) {
          loadAvailableCalendars();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadAvailableCalendars]);

  const startOAuth = useCallback(async () => {
    setError(null);

    if (prefersSameTabOAuth()) {
      try {
        setIsAuthorizing(true);
        sessionStorage.setItem(
          CALENDAR_OAUTH_RETURN_PATH,
          window.location.pathname + window.location.search
        );
      } catch {
        // Private mode — the callback falls back to a default path.
      }
      try {
        const authUrl = await calendarManager.getAuthorizationUrl(
          `${window.location.origin}/calendar-callback`
        );
        window.location.href = authUrl;
      } catch (err) {
        console.error('Failed to start Google sign-in:', err);
        setError('We could not open Google sign-in. Please try again.');
        setIsAuthorizing(false);
      }
      return;
    }

    // `window.open` has to run inside the gesture, so the popup is created
    // first and pointed at the real URL once it has been fetched.
    const popup = window.open(
      'about:blank',
      'google-calendar-auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    if (!popup || popup.closed) {
      setError('Your browser blocked the Google sign-in window. Allow pop-ups and try again.');
      return;
    }

    // Hoisted handlers below lose the null-narrowing on `popup`, so bind the
    // checked reference once.
    const authWindow = popup;
    setIsAuthorizing(true);

    function cleanup() {
      clearInterval(closedTimer);
      window.removeEventListener('message', onMessage);
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const data = event.data as CalendarOAuthResult | undefined;
      if (data?.type === 'OAUTH_SUCCESS') {
        cleanup();
        authWindow.close();
        completeAuthorization(data.code, data.state);
      } else if (data?.type === 'OAUTH_ERROR') {
        cleanup();
        authWindow.close();
        setIsAuthorizing(false);
        setError('Google sign-in was cancelled. You can try again below.');
      }
    }

    const closedTimer = setInterval(() => {
      if (authWindow.closed) {
        cleanup();
        setIsAuthorizing(false);
      }
    }, 800);

    window.addEventListener('message', onMessage);

    try {
      const authUrl = await calendarManager.getAuthorizationUrl(
        `${window.location.origin}/calendar-callback`
      );
      if (authWindow.closed) {
        cleanup();
        setIsAuthorizing(false);
        return;
      }
      authWindow.location.href = authUrl;
    } catch (err) {
      console.error('Failed to start Google sign-in:', err);
      cleanup();
      if (!authWindow.closed) authWindow.close();
      setError('We could not open Google sign-in. Please try again.');
      setIsAuthorizing(false);
    }
  }, [completeAuthorization]);

  const toggleCalendar = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const confirmSelection = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setIsBinding(true);
    setError(null);
    try {
      const defaultAccountEmail = resolveAccountEmail(available) ?? undefined;
      for (const googleCalendarId of selectedIds) {
        // Each calendar connects against the Google account it was listed from.
        const googleAccountEmail =
          available.find(c => c.id === googleCalendarId)?.accountEmail ?? defaultAccountEmail;
        await calendarManager.connectToGoogleCalendar({
          googleCalendarId,
          syncDirection: SyncDirection.IMPORT_ONLY,
          googleAccountEmail,
        });
      }
      await refreshIntegration();
      setPickerState('idle');
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to connect calendars:', err);
      setError('We could not connect those calendars. Please try again.');
    } finally {
      setIsBinding(false);
    }
  }, [selectedIds, available, refreshIntegration]);

  const setAffectsAvailability = async (calendarId: string, next: boolean) => {
    setInfo(prev =>
      prev
        ? {
            ...prev,
            connectedCalendars: (prev.connectedCalendars ?? []).map(c =>
              c.calendarId === calendarId ? { ...c, affectsAvailability: next } : c
            ),
          }
        : prev
    );
    try {
      await calendarManager.setCalendarAffectsAvailability(calendarId, next);
    } catch {
      await refreshIntegration();
      setError('That change did not stick. Please try again.');
    }
  };

  const setWriteTarget = async (calendarId: string) => {
    try {
      if (calendarId) {
        await calendarManager.setWriteTargetCalendar(calendarId);
      } else {
        await calendarManager.clearWriteTargetCalendar();
      }
      await refreshIntegration();
    } catch {
      setError('We could not change where new appointments are added.');
    }
  };

  const addAnother = () => {
    setPickerState('loading');
    loadAvailableCalendars();
  };

  if (stage === 'loading') {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  const writeTargets = syncedCalendars.filter(c => c.hasWriteScope && !c.writeAccessDenied);
  const currentWriteTarget = syncedCalendars.find(c => c.isWriteTarget);
  const accountEmail = resolveAccountEmail(available);

  return (
    <div className="space-y-4">
      <StepIntro>
        Routemize reads your existing events so customers can only book times you're actually
        free. Nothing is shared with them — they just see fewer slots.
      </StepIntro>

      {error && (
        <Callout tone="danger" icon={AlertTriangle}>
          {error}
        </Callout>
      )}

      {stage === 'needs-auth' && (
        <div className="rounded-xl border border-ink-200 bg-surface p-5 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border border-ink-200 bg-ink-50">
            <GoogleMark className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold text-ink-900">Connect Google Calendar</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-500">
            You'll be asked to sign in and tick the calendar permission. Make sure that box is
            checked, or we won't be able to see your calendars.
          </p>
          <div className="mt-4">
            <Button onClick={startOAuth} loading={isAuthorizing} className="w-full sm:w-auto">
              <GoogleMark className="h-4 w-4" />
              Connect Google Calendar
            </Button>
          </div>
        </div>
      )}

      {stage === 'choosing' && (
        <div className="rounded-xl border-2 border-accent-500 bg-surface">
          <div className="flex items-center gap-2 border-b border-ink-100 bg-accent-50 px-4 py-3">
            <ShieldCheck className="h-4 w-4 flex-shrink-0 text-accent-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900">
                Your Google account is connected
              </p>
              <p className="truncate text-xs text-ink-600">
                {accountEmail
                  ? `Signed in as ${accountEmail}. Now choose which calendars to check.`
                  : 'Now choose which calendars to check.'}
              </p>
            </div>
          </div>

          <div className="p-4">
            {pickerState === 'loading' && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading your calendars…
              </div>
            )}

            {(pickerState === 'empty' || pickerState === 'error') && (
              <div className="space-y-3">
                <Callout tone="warning" icon={AlertTriangle}>
                  <span className="font-semibold">
                    {pickerState === 'empty'
                      ? "Google didn't share any calendars with us."
                      : "We couldn't load your calendars."}
                  </span>{' '}
                  {pickerState === 'empty'
                    ? 'Signing in with Google does not always share your calendars, even if the permission box was checked. Reconnect to grant calendar access.'
                    : 'Your Google access may have expired.'}
                </Callout>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button onClick={startOAuth} loading={isAuthorizing}>
                    <RefreshCw className="h-4 w-4" />
                    Reconnect Google
                  </Button>
                  <Button variant="secondary" onClick={loadAvailableCalendars}>
                    Try again
                  </Button>
                </div>
              </div>
            )}

            {pickerState === 'ready' && (
              <>
                <ul className="space-y-2">
                  {available.map(calendar => {
                    const id = calendar.id ?? '';
                    const checked = selectedIds.includes(id);
                    return (
                      <li key={id}>
                        <label
                          className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
                            checked
                              ? 'border-accent-500 bg-accent-50'
                              : 'border-ink-200 bg-surface hover:border-ink-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCalendar(id)}
                            className="h-5 w-5 flex-shrink-0 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="text-sm font-medium text-ink-900">
                                {calendarDisplayName(calendar)}
                              </span>
                              {calendar.isPrimary && (
                                <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-2xs font-semibold text-accent-700">
                                  Main calendar
                                </span>
                              )}
                            </span>
                            {calendar.id && calendar.id !== calendarDisplayName(calendar) && (
                              <span className="mt-0.5 block truncate text-xs text-ink-500">
                                {calendar.id}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>

                <Button
                  onClick={confirmSelection}
                  loading={isBinding}
                  disabled={selectedIds.length === 0}
                  className="mt-4 w-full"
                >
                  <Check className="h-4 w-4" />
                  {selectedIds.length > 1
                    ? `Sync these ${selectedIds.length} calendars`
                    : 'Sync this calendar'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {stage === 'connected' && (
        <>
          <div className="overflow-hidden rounded-xl border border-success-200 bg-surface">
            <div className="flex items-center gap-2 border-b border-success-100 bg-success-50 px-4 py-3">
              <CalendarCheck className="h-4 w-4 flex-shrink-0 text-success-600" />
              <p className="text-sm font-semibold text-success-800">
                {syncedCalendars.length === 1
                  ? '1 calendar is syncing'
                  : `${syncedCalendars.length} calendars are syncing`}
              </p>
            </div>

            <ul className="divide-y divide-ink-100">
              {syncedCalendars.map(calendar => {
                const affects = calendar.affectsAvailability ?? true;
                const labelId = `cal-affects-${calendar.calendarId}`;
                return (
                  <li
                    key={calendar.calendarId}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
                  >
                    <Calendar className="h-4 w-4 flex-shrink-0 text-ink-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink-900">
                        {calendar.externalCalendarName || 'Connected calendar'}
                      </p>
                      <p id={labelId} className="text-xs text-ink-500">
                        {affects ? 'Blocks your availability' : 'Shown, but never blocks bookings'}
                      </p>
                    </div>
                    <Switch
                      size="sm"
                      checked={affects}
                      aria-labelledby={labelId}
                      disabled={!calendar.calendarId}
                      onCheckedChange={next =>
                        calendar.calendarId && setAffectsAvailability(calendar.calendarId, next)
                      }
                    />
                  </li>
                );
              })}
            </ul>

            <div className="border-t border-ink-100 px-4 py-3">
              <Button variant="secondary" size="sm" onClick={addAnother}>
                <Plus className="h-4 w-4" />
                Add another calendar
              </Button>
            </div>
          </div>

          {writeTargets.length > 0 && (
            <div className="rounded-xl border border-ink-200 bg-surface p-4">
              <label
                htmlFor="onboarding-write-target"
                className="mb-1.5 block text-sm font-semibold text-ink-900"
              >
                Add new Routemize appointments to
              </label>
              <p className="mb-2 text-xs text-ink-500">
                Bookings you take will also appear on this Google calendar.
              </p>
              <Select
                id="onboarding-write-target"
                value={currentWriteTarget?.calendarId ?? ''}
                onChange={e => setWriteTarget(e.target.value)}
              >
                <option value="">Don't add them to a calendar</option>
                {writeTargets.map(c => (
                  <option key={c.calendarId} value={c.calendarId ?? ''}>
                    {c.externalCalendarName || c.calendarId}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {pickerState === 'ready' && available.length > 0 && (
            <div className="rounded-xl border-2 border-accent-500 bg-surface p-4">
              <p className="mb-3 text-sm font-semibold text-ink-900">Add another calendar</p>
              <ul className="space-y-2">
                {available
                  .filter(c => !syncedCalendars.some(s => s.externalCalendarId === c.id))
                  .map(calendar => {
                    const id = calendar.id ?? '';
                    const checked = selectedIds.includes(id);
                    return (
                      <li key={id}>
                        <label
                          className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
                            checked
                              ? 'border-accent-500 bg-accent-50'
                              : 'border-ink-200 bg-surface hover:border-ink-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCalendar(id)}
                            className="h-5 w-5 flex-shrink-0 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                          />
                          <span className="min-w-0 text-sm font-medium text-ink-900">
                            {calendarDisplayName(calendar)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
              </ul>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={confirmSelection}
                  loading={isBinding}
                  disabled={selectedIds.length === 0}
                >
                  <Check className="h-4 w-4" />
                  Add selected
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPickerState('idle');
                    setSelectedIds([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {stage !== 'connected' && (
        <div className="text-center">
          {hasSkipped ? (
            <Callout tone="info" icon={Calendar}>
              No calendar connected. You can set this up any time from Settings — until then,
              Routemize won't know about events booked outside it.
            </Callout>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setHasSkipped(true)}>
              Skip — I'll connect a calendar later
            </Button>
          )}
        </div>
      )}

      {stage === 'connected' && (
        <Callout tone="warning" icon={AlertTriangle}>
          <span className="font-semibold">Events marked "Busy" block bookings.</span> All-day
          events often default to Busy and can hide an entire day. If your calendar looks full
          after syncing, switch those events to "Free" in Google Calendar.
        </Callout>
      )}

      {/* Last screen in the wizard, so it has to say where Get started leads. */}
      <Callout tone="info" icon={ArrowRight}>
        <span className="font-semibold text-ink-800">Last step.</span> Get started takes you to
        your booking form, where you set your appointment times and the questions customers
        answer. Finish that and your booking link is ready to share.
      </Callout>
    </div>
  );
};
